<template>
  <div class="sale-step">
    <b-alert :show="multiSale" variant="warning" class="p-1 mb-2">
      <small>{{ $t('catalog.sale.multi_sale_warning') }}</small>
    </b-alert>

    <!-- Preço -->
    <div class="sale-step-section">
      <h6 class="sale-step-heading">
        <feather-icon icon="DollarSignIcon" class="mr-50" svgClasses="w-4 h-4"/>
        {{ subscriptionPlan ? $t('product.subscription_price') : $t('catalog.sale.price') }}
      </h6>
      <small class="text-muted d-block mb-1">
        <template v-if="subscriptionPlan">
          {{ $t('product.subscription_price_help', { interval: (subscriptionPlan.interval_label || '').toLowerCase() }) }}
        </template>
        <template v-else>{{ priceHint || $t('catalog.sale.price_hint') }}</template>
      </small>
      <div class="sale-step-price">
        <money
          id="sale_price"
          :value="localPrice"
          v-bind="moneyMask"
          class="form-control required"
          @focus.native="priceTouched = true"
          @input="onPriceInput"/>
      </div>
      <small v-if="Number(price) === 0" class="text-warning d-block mt-1">
        {{ $t('catalog.sale.free_notice') }}
      </small>

      <!-- Registro no gateway (só quando o item é um plano) -->
      <div class="mt-1" v-if="subscriptionPlan">
        <b-badge :variant="gatewayStatus.variant">{{ gatewayStatus.label }}</b-badge>
        <small v-if="gatewayStatus.hint" class="d-block mt-1" :class="gatewayStatus.hintClass">
          {{ gatewayStatus.hint }}
        </small>
      </div>
    </div>

    <!-- Fotos -->
    <div class="sale-step-section" v-if="showPhotos">
      <h6 class="sale-step-heading">
        <feather-icon icon="ImageIcon" class="mr-50" svgClasses="w-4 h-4"/>
        {{ $t('catalog.sale.photos') }}
      </h6>
      <small class="text-muted d-block mb-1">{{ photosHint || $t('catalog.sale.photos_hint') }}</small>

      <vue-dropzone
        v-if="productId"
        id="sale_step_upload"
        ref="fileUploadZone"
        :key="`dz-${productId}`"
        v-on:vdropzone-success="onUploadSuccess"
        v-on:vdropzone-error="onUploadError"
        v-on:vdropzone-queue-complete="onUploadComplete"
        :use-font-awesome="true"
        :options="dropzoneOptions"
        :use-custom-dropzone-options="true"
        :useCustomSlot="true"
        class="sale-step-dropzone">
        <div class="dropzone-custom-content">
          <h4 class="dropzone-custom-title">{{ $t('messages.drag_and_drop_file') }}</h4>
          <div class="subtitle">{{ $t('messages.or_select_file_in_computer') }}</div>
        </div>
      </vue-dropzone>

      <div class="sale-step-photos" v-if="files.length">
        <div class="sale-step-photo" v-for="media in files" :key="media.id">
          <img v-if="mediaUrl(media)" :src="mediaUrl(media)" :alt="media.file_name"/>
          <div v-else class="sale-step-photo-fallback">
            <feather-icon icon="FileIcon" svgClasses="w-6 h-6"/>
          </div>
          <button
            type="button"
            class="sale-step-photo-remove"
            :title="$t('catalog.sale.remove_photo')"
            @click="confirmDelete(media)">
            <feather-icon icon="XIcon" svgClasses="w-4 h-4"/>
          </button>
        </div>
      </div>
      <small v-else class="text-muted d-block mt-1">{{ $t('catalog.sale.no_photos') }}</small>
    </div>

    <!-- Slot para blocos específicos do tipo (ex.: logo do espaço) -->
    <slot/>

    <!-- Textos opcionais do anúncio -->
    <div class="sale-step-section">
      <button type="button" class="sale-step-toggle" @click="textsOpen = !textsOpen">
        <feather-icon :icon="textsOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'" svgClasses="w-4 h-4" class="mr-50"/>
        <span>{{ $t('catalog.sale.texts_optional') }}</span>
      </button>
      <small class="text-muted d-block mb-1">{{ $t('catalog.sale.texts_hint') }}</small>

      <div v-if="textsOpen" class="mt-1">
        <div class="w-full mb-2" v-if="showDescription">
          <label class="text-sm pl-1">{{ $t('catalog.sale.description_advanced') }}</label>
          <ComplexEditor :editor_data="description" @update:editor_data="$emit('update:description', $event)"/>
        </div>
        <div class="w-full mb-2">
          <label class="text-sm pl-1">{{ $t('product.how_works') }}</label>
          <small class="text-muted d-block">{{ $t('catalog.sale.how_works_hint') }}</small>
          <ComplexEditor :editor_data="howWorks" @update:editor_data="$emit('update:howWorks', $event)"/>
        </div>
        <div class="w-full">
          <label class="text-sm pl-1">{{ $t('product.guidelines') }}</label>
          <small class="text-muted d-block">{{ $t('catalog.sale.guidelines_hint') }}</small>
          <ComplexEditor :editor_data="guidelines" @update:editor_data="$emit('update:guidelines', $event)"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import { BAlert, BBadge } from 'bootstrap-vue'
import { Money } from 'v-money'

import standard from '@/services/standard'
import ComplexEditor from '@/components/ComplexEditor.vue'

/**
 * Passo "Preço e fotos": a parte de PRODUTO do cadastro do item, sem nunca
 * dizer "produto" para o usuário. O formulário dono (mixin productBinding)
 * garante que `productId` existe antes deste passo ficar acessível — upload
 * de foto vai direto para a API e precisa do id.
 *
 * A descrição do anúncio fica escondida a menos que já exista conteúdo
 * (produto legado): para item novo, a descrição que o comprador vê é a do
 * próprio item, e oferecer duas caixas de "descrição" só gerava dúvida.
 */
export default {
  name: 'SaleStep',
  components: {
    vueDropzone: vue2Dropzone,
    BAlert,
    BBadge,
    ComplexEditor,
    Money,
  },
  props: {
    productId: { type: Number, default: null },
    price: { type: [Number, String], default: null },
    description: { type: String, default: '' },
    howWorks: { type: String, default: '' },
    guidelines: { type: String, default: '' },
    files: { type: Array, default: () => [] },
    subscriptionPlan: { type: Object, default: null },
    registerOnGateway: { type: Boolean, default: false },
    gatewayRegistered: { type: Boolean, default: false },
    multiSale: { type: Boolean, default: false },
    showPhotos: { type: Boolean, default: true },
    priceHint: { type: String, default: null },
    photosHint: { type: String, default: null },
  },
  data() {
    return {
      textsOpen: false,
      /**
       * Espelho local do preço.
       *
       * A diretiva do v-money formata o campo e dispara `change` já na
       * montagem, e o componente traduz isso num `input` — ou seja, um preço
       * "digitado" que ninguém digitou. Com o `.sync` direto, esse eco escrevia
       * 0 no formulário sempre que o passo abria antes de o anúncio carregar, e
       * o aviso de "defina o preço" nunca aparecia porque o campo já não estava
       * mais vazio. Aqui o eco morre no espelho e só mudança real sobe.
       */
      localPrice: this.toNumber(this.price),
      /** O usuário já tocou no campo? Separa digitação de eco da máscara. */
      priceTouched: false,
      moneyMask: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false,
      },
    }
  },
  watch: {
    price(value) {
      const next = this.toNumber(value)
      if (next !== this.localPrice) {
        this.localPrice = next
      }
    },
  },
  computed: {
    dropzoneOptions() {
      return {
        url: `${process.env.VUE_APP_API_BASE_URL}/api/v1/products/${this.productId}/files`,
        thumbnailWidth: 150,
        addRemoveLinks: false,
        createImageThumbnails: true,
        timeout: 180000,
        headers: { Authorization: `Bearer ${standard.token()}` },
        acceptedFiles: 'image/jpeg,image/png',
      }
    },
    /** Produto legado com descrição própria continua editável; item novo não vê o campo. */
    showDescription() {
      return !!(this.description && String(this.description).trim())
    },
    gatewayStatus() {
      if (!this.registerOnGateway) {
        return {
          variant: 'secondary',
          label: this.$t('subscription_plan.gateway_not_applicable'),
          hint: null,
        }
      }
      if (this.gatewayRegistered) {
        return {
          variant: 'success',
          label: this.$t('subscription_plan.gateway_registered'),
          hint: null,
        }
      }
      return {
        variant: 'warning',
        label: this.$t('subscription_plan.gateway_pending'),
        hint: this.$t('subscription_plan.gateway_pending_help'),
        hintClass: 'text-warning',
      }
    },
  },
  mounted() {
    // Produto legado com descrição: abre a seção para o conteúdo não "sumir".
    if (this.showDescription) {
      this.textsOpen = true
    }
  },
  methods: {
    toNumber(value) {
      if (value === null || value === undefined || value === '') return 0
      const parsed = Number(value)
      return Number.isNaN(parsed) ? 0 : parsed
    },
    onPriceInput(value) {
      const next = this.toNumber(value)
      this.localPrice = next
      // Sobe quando o usuário mexeu no campo (aí até "0" é escolha dele, que a
      // tela avisa que sai como gratuita) ou quando o número mudou de fato. O
      // eco da formatação inicial não passa por nenhum dos dois.
      if (this.priceTouched || this.toNumber(this.price) !== next) {
        this.$emit('update:price', next)
      }
    },
    mediaUrl(media) {
      return media.display_url || media.original_url || media.thumbnail_url || null
    },
    onUploadSuccess(file, data) {
      this.$emit('file-uploaded', data)
      this.notifySuccess(this.$vs, this.$t('messages.success'))
    },
    onUploadError(file) {
      if (file.status === 'error') {
        this.notifyError(this.$vs, this.$t('messages.error'))
      }
    },
    onUploadComplete() {
      if (this.$refs.fileUploadZone) {
        this.$refs.fileUploadZone.removeAllFiles()
      }
    },
    confirmDelete(media) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirmação',
        acceptText: 'Sim',
        cancelText: 'Não',
        text: this.$t('catalog.sale.remove_photo_confirm'),
        accept: () => this.$emit('delete-file', media),
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.sale-step-section {
  padding: 1rem 0;

  & + .sale-step-section {
    border-top: 1px dashed rgba(0, 0, 0, 0.1);
  }
}

.sale-step-heading {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.sale-step-price {
  max-width: 16rem;
}

.sale-step-dropzone {
  width: 100%;
  margin-bottom: 0.75rem;
  min-height: 120px;
}

.sale-step-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.sale-step-photo {
  position: relative;
  width: 9.5rem;
  height: 5.5rem;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.04);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.sale-step-photo-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
}

.sale-step-photo-remove {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  border: none;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;

  &:hover {
    background: #ea5455;
  }
}

.sale-step-toggle {
  display: flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 0;
  font-weight: 600;
  cursor: pointer;
  color: inherit;
}
</style>
